import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { GLOBAL_BENEFITS, SMART_LINKS } from "../../data/smartContent";
import { NATIONAL_PARTNERS, LINKS } from "../../data/content";
import { NationalBenefitsCarousel } from "../shared/NationalBenefitsCarousel";

/* ---------- Global carousel (neschimbat) ---------- */
const NavButton = ({ direction, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={direction === "left" ? "Înapoi" : "Înainte"}
    data-testid={testId}
    className={`hidden md:flex absolute top-1/2 -translate-y-1/2 z-10 h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-ink shadow-sm hover:bg-slate-50 transition-colors ${
      direction === "left" ? "-left-3" : "-right-3"
    }`}
  >
    {direction === "left" ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
  </button>
);

const Carousel = ({ testId, children }) => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.8, behavior: "smooth" });
  return (
    <div className="relative" data-testid={testId}>
      <NavButton direction="left" onClick={() => scroll(-1)} testId={`${testId}-prev`} />
      <div ref={ref} className="flex gap-5 overflow-x-auto scroll-smooth snap-x pb-2 no-scrollbar">
        {children}
      </div>
      <NavButton direction="right" onClick={() => scroll(1)} testId={`${testId}-next`} />
    </div>
  );
};

/* ---------- National carousel (identic cu /arcca) ---------- */
const SectionHead = ({ label, title, subtitle }) => (
  <div className="max-w-3xl">
    <Reveal>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-isic">{label}</p>
    </Reveal>
    <Reveal delay={0.08}>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold leading-[1.15] text-ink">{title}</h2>
    </Reveal>
    <Reveal delay={0.16}>
      <p className="mt-5 text-lg leading-relaxed text-slate-600">{subtitle}</p>
    </Reveal>
  </div>
);

export const SmartBenefitsNational = () => (
  <section data-testid="section-benefits-national" className="bg-slate-50 py-14 lg:py-20">
    <div className="mx-auto max-w-7xl px-5 lg:px-10">
      <SectionHead
        label="Beneficii ISIC în România"
        title="Câteva exemple de reduceri cu BRD-ISIC la nivel național"
        subtitle="Descoperă beneficii pe care le poți folosi zi de zi, cu cardul tău BRD-ISIC."
      />
      <div className="mt-10">
        <NationalBenefitsCarousel
          items={NATIONAL_PARTNERS}
          allLink={LINKS.discountsRo}
          testIdPrefix="national"
          cardHoverClass="hover:border-isic/40"
          textClass="text-ink"
          fallbackClass="font-serif font-bold text-lg text-ink text-center"
          buttonClass="text-ink hover:border-isic hover:text-isic-dark"
        />
      </div>
    </div>
  </section>
);

export const SmartBenefitsGlobal = () => (
  <section data-testid="section-benefits-global" className="bg-white py-14 lg:py-20">
    <div className="mx-auto max-w-7xl px-5 lg:px-10">
      <SectionHead
        label="Beneficii ISIC la nivel global"
        title="Câteva exemple de reduceri cu BRD-ISIC la nivel global"
        subtitle="Când călătorești sau studiezi în altă țară, BRD-ISIC îți oferă acces la beneficiile ISIC din întreaga lume."
      />
      <div className="mt-10">
        <Carousel testId="global-carousel">
          {GLOBAL_BENEFITS.map((d, i) => (
            <div
              key={d.image}
              data-testid={`global-item-${i}`}
              className="snap-start shrink-0 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white"
            >
              <img alt={d.alt} className="w-full h-auto" loading="lazy" src={d.image} />
            </div>
          ))}
        </Carousel>
      </div>
      <div className="mt-8 flex justify-center">
        <a
          href={SMART_LINKS.globalMore}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="global-more"
          className="inline-flex items-center rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-ink hover:border-slate-400 transition-colors"
        >
          Mai multe reduceri internaționale
        </a>
      </div>
    </div>
  </section>
);
